/**
 * What the Guest Selector's fee tag says, given the price quote.
 *
 * A guest's cost depends on the bay, not only on the booker's plan: a bay that
 * charges per person bills each guest its hourly rate and carries no guest fee.
 * The page cannot tell from the bay (the public bay response does not expose its
 * pricing), so the quote's time fee is the source — the same answer the ticket
 * card prices from. Until a quote lands the tag is a placeholder, never the plan's
 * guest fee, which on a per-person bay is a price that will not be charged.
 *
 * Shared by the web Guest Selector and the mobile Guests sheet, so both read a
 * per-person bay the same way.
 *
 * @module domains/booking/GuestSelector/guestFeeNote
 */

import type { ApiFeeItem } from '@lgc/database/types';

/** The plan's (or, with no plan, the club's) guest-fee policy. */
export interface GuestFeePolicy {
  enabled: boolean;
  freeGuests: number;
  feePerGuestCents: number;
}

export interface GuestFeeNoteInputs {
  policy: GuestFeePolicy;
  /** The quote's fees, in render order. Empty while there is no quote. */
  fees: readonly ApiFeeItem[];
  /** The quote is enabled: a bay and a time are chosen. */
  quoteEnabled: boolean;
  /** A quote for the current selection has arrived. */
  hasQuote: boolean;
  /** The quote request is in flight. */
  quoteLoading: boolean;
}

/** The fee props `GuestSelector` takes. */
export interface GuestFeeNote {
  showFeeInfo: boolean;
  feeInfoPending: boolean;
  freeGuests: number;
  feePerGuestCents: number;
  perPersonRateCents?: number;
}

export function deriveGuestFeeNote({
  policy, fees, quoteEnabled, hasQuote, quoteLoading,
}: GuestFeeNoteInputs): GuestFeeNote {
  const policyNote = {
    freeGuests: policy.freeGuests,
    feePerGuestCents: policy.feePerGuestCents,
  };

  // No bay or time yet: nothing is being priced, and the plan's policy is all there is.
  if (!quoteEnabled) return { ...policyNote, showFeeInfo: policy.enabled, feeInfoPending: false };

  if (!hasQuote) {
    // Still coming → a placeholder. Not coming (the quote failed) → no tag: the price
    // is unknown, and the ticket card says why.
    return { ...policyNote, showFeeInfo: false, feeInfoPending: quoteLoading };
  }

  const perPerson = fees.find(
    (f) => f.details.detailType === 'time' && f.details.pricingBasis === 'person',
  );
  if (perPerson && perPerson.details.detailType === 'time') {
    const rate = perPerson.details.ratePerPersonCents;
    // A per-person bay never charges a guest fee, so the plan's policy cannot stand
    // in for a missing rate — show nothing rather than a wrong number.
    return rate === undefined
      ? { ...policyNote, showFeeInfo: false, feeInfoPending: false }
      : { ...policyNote, showFeeInfo: true, feeInfoPending: false, perPersonRateCents: rate };
  }

  return { ...policyNote, showFeeInfo: policy.enabled, feeInfoPending: false };
}
